/**
 * EU AI Act — Catálogo de Requisitos artigo-a-artigo.
 * Route: /ai-act/requirements
 */
import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { BookOpen, CheckCircle2, XCircle, Clock, MinusCircle, HelpCircle, Search, Loader2, ExternalLink } from 'lucide-react';

const STATUS_META = {
  conforme: { label: 'Conforme', icon: CheckCircle2, tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', color: 'text-emerald-600' },
  em_progresso: { label: 'Em Progresso', icon: Clock, tone: 'bg-blue-50 text-blue-700 border-blue-200', color: 'text-blue-600' },
  nao_conforme: { label: 'Não Conforme', icon: XCircle, tone: 'bg-red-50 text-red-700 border-red-200', color: 'text-red-600' },
  nao_aplicavel: { label: 'Não Aplicável', icon: MinusCircle, tone: 'bg-slate-50 text-slate-600 border-slate-200', color: 'text-slate-500' },
  nao_avaliado: { label: 'Não Avaliado', icon: HelpCircle, tone: 'bg-slate-50 text-slate-500 border-slate-200', color: 'text-slate-400' },
};

const CATEGORY_LABEL = {
  prohibited: 'Práticas Proibidas',
  classification: 'Classificação',
  risk: 'Gestão de Risco',
  data: 'Governança de Dados',
  doc: 'Documentação Técnica',
  audit: 'Registo/Audit Trail',
  transparency: 'Transparência',
  oversight: 'Supervisão Humana',
  robustness: 'Robustez/Cibersegurança',
  quality: 'Sistema de Qualidade',
  deployer: 'Obrigações Deployer',
  fria: 'FRIA',
  gpai: 'GPAI',
  postmarket: 'Pós-Comercialização',
};

const SEVERITY_META = {
  critical: { label: 'Crítico', tone: 'bg-red-50 text-red-700 border-red-200' },
  high: { label: 'Alto', tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  medium: { label: 'Médio', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  low: { label: 'Baixo', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export default function AiActRequirements() {
  const [loading, setLoading] = useState(true);
  const [requirements, setRequirements] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => { document.title = 'Requisitos EU AI Act · NIS2AI'; }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (categoryFilter !== 'all') params.category = categoryFilter;
        if (statusFilter !== 'all') params.compliance_status = statusFilter;
        const [listRes, statsRes] = await Promise.all([
          api.get('/ai-act/requirements', { params }),
          api.get('/ai-act/requirements/stats'),
        ]);
        if (ignore) return;
        setRequirements(Array.isArray(listRes.data) ? listRes.data : []);
        setStats(statsRes.data);
      } catch (err) {
        if (!ignore) toast.error('Falha ao carregar requisitos AI Act.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [categoryFilter, statusFilter, refreshKey]);

  const openEdit = (req) => {
    setSelected(req);
    setNewStatus(req.compliance_status || 'nao_avaliado');
    setNotes(req.compliance_notes || '');
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/ai-act/requirements/${selected.requirement_id}/status`, {
        status: newStatus,
        notes: notes || null,
      });
      toast.success('Estado atualizado.');
      setSelected(null);
      setRefreshKey(k => k + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao atualizar.');
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return requirements;
    const q = search.toLowerCase();
    return requirements.filter(r =>
      (r.title || '').toLowerCase().includes(q) ||
      (r.code || '').toLowerCase().includes(q) ||
      (r.article_ref || '').toLowerCase().includes(q) ||
      (r.description || '').toLowerCase().includes(q)
    );
  }, [requirements, search]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(r => {
      const c = r.category || 'outros';
      if (!g[c]) g[c] = [];
      g[c].push(r);
    });
    return g;
  }, [filtered]);

  return (
    <Layout title="EU AI Act — Requisitos">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="ai-act-requirements-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-indigo-600" /> Requisitos EU AI Act
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Catálogo estruturado dos 16 artigos-chave do Regulamento (UE) 2024/1689. Atualize o estado de conformidade e ligue evidências.
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card data-testid="kpi-total">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">Total</div>
              <div className="text-3xl font-bold text-slate-900 tabular-nums mt-2">{stats?.buckets.total ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">Conformidade: <span className="font-semibold text-indigo-600">{stats?.conformity_pct ?? 0}%</span></div>
            </CardContent>
          </Card>
          {['conforme', 'em_progresso', 'nao_conforme', 'nao_avaliado'].map(k => {
            const meta = STATUS_META[k];
            const Icon = meta.icon;
            return (
              <Card key={k} data-testid={`kpi-${k}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">{meta.label}</div>
                    <Icon className={`h-4 w-4 ${meta.color}`} />
                  </div>
                  <div className="text-3xl font-bold text-slate-900 tabular-nums">{stats?.buckets[k] ?? 0}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Pesquisar por título, artigo ou descrição…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9"
                data-testid="airequirements-search"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-52" data-testid="airequirements-filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                {Object.entries(CATEGORY_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44" data-testid="airequirements-filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os estados</SelectItem>
                {Object.entries(STATUS_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* List grouped by category */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-slate-400 py-16 text-sm">Sem requisitos correspondentes.</div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([category, reqs]) => (
              <div key={category} data-testid={`airequirements-group-${category}`}>
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{CATEGORY_LABEL[category] || category}</h2>
                  <span className="text-xs text-slate-400">({reqs.length})</span>
                </div>
                <div className="space-y-2">
                  {reqs.map(req => {
                    const status = STATUS_META[req.compliance_status] || STATUS_META.nao_avaliado;
                    const sev = SEVERITY_META[req.severity] || SEVERITY_META.medium;
                    const Icon = status.icon;
                    return (
                      <Card key={req.requirement_id} className="hover:border-indigo-200 transition-colors cursor-pointer" onClick={() => openEdit(req)} data-testid={`airequirement-${req.requirement_id}`}>
                        <CardContent className="p-4 flex gap-4 items-start">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${status.tone}`}>
                            <Icon className={`h-5 w-5 ${status.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-mono text-xs text-slate-500">{req.code}</span>
                              <span className="text-slate-300">·</span>
                              <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">{req.article_ref}</Badge>
                              {req.annex_ref && <Badge variant="secondary" className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">{req.annex_ref}</Badge>}
                              <Badge variant="secondary" className={`${sev.tone} text-[10px]`}>{sev.label}</Badge>
                              <Badge variant="secondary" className={`${status.tone} text-[10px] ml-auto`}>{status.label}</Badge>
                            </div>
                            <h3 className="font-semibold text-slate-900 text-sm mb-1">{req.title}</h3>
                            <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">{req.description}</p>
                            {req.compliance_notes && (
                              <div className="mt-2 text-xs text-slate-500 bg-slate-50 rounded p-2 border-l-2 border-slate-300">
                                📝 {req.compliance_notes}
                              </div>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                              <span>Aplica-se a: {(req.applies_to || []).join(', ')}</span>
                              <a
                                href={`https://eur-lex.europa.eu/eli/reg/2024/1689/oj`}
                                target="_blank" rel="noopener noreferrer"
                                className="text-indigo-600 hover:underline inline-flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Ver artigo <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selected?.title}</DialogTitle>
            <DialogDescription>
              {selected?.code} · {selected?.article_ref}{selected?.annex_ref ? ` · ${selected.annex_ref}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-slate-700 leading-relaxed bg-slate-50 rounded p-3">
              {selected?.description}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Estado de conformidade</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="airequirement-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Notas / Justificação</label>
              <Textarea
                rows={4}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Descrever medidas implementadas, controlos, evidências…"
                data-testid="airequirement-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelected(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="airequirement-save-btn">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A guardar…</> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
